import type { FilterOperator, IOperationResultNoValue, IProjectedContact, IRawRichTextContentState } from '@ViewModels';

/** Which date the activities-list date-window filter measures against. */
export enum VolunteerActivityDateBasis {
	/** Activities with a shift overlapping the window. */
	Shift = 'Shift',
	/** Activities whose linked event's date overlaps the window. */
	Event = 'Event',
}

/**
 * Derived, not stored. The T-01 API model has no `Type` field: an activity with a set `EndAt` is
 * bounded (Scheduled); one with no `EndAt` is open-ended (Ongoing).
 */
export enum VolunteerActivityStatus {
	Scheduled = 'Scheduled',
	Ongoing = 'Ongoing',
}

/**
 * Mirrors the T-01 API model `Levitate.Api.Models.Volunteers.VolunteerActivity`. JSON is camelCase.
 * `accountId`/`creatorId` are intentionally not exposed by the API. `description` is a rich Content
 * value; a null/absent `endAt` means the activity is open-ended (Ongoing).
 */
export interface IVolunteerActivity {
	id: string;
	title?: string;
	description?: IRawRichTextContentState;
	location?: string;
	startAt?: string;
	endAt?: string;
	timeZone?: string;
	eventId?: string;
	signupSurveyId?: string;
	isArchived?: boolean;
}

export const deriveActivityStatus = (activity: Pick<IVolunteerActivity, 'endAt'>): VolunteerActivityStatus =>
	activity.endAt ? VolunteerActivityStatus.Scheduled : VolunteerActivityStatus.Ongoing;

/** The API returns 403 on every /volunteering/* endpoint when the account's volunteer board is disabled. */
export const isVolunteerBoardDisabledError = (error: unknown): boolean =>
	(error as IOperationResultNoValue | undefined)?.systemCode === 403;

export const isVolunteerNotFoundError = (error: unknown): boolean =>
	(error as IOperationResultNoValue | undefined)?.systemCode === 404;

/** Mirrors the T-02 API model `Levitate.Api.Models.Volunteers.VolunteerRole`. Account-level, unique by name. */
export interface IVolunteerRole {
	id: string;
	name: string;
	isArchived?: boolean;
}

/** One job a shift needs filled, with its own optional headcount. `capacity` null/omitted = unlimited. */
export interface IVolunteerShiftRole {
	roleId: string;
	capacity?: number | null;
}

/**
 * Mirrors the T-02 API model `Levitate.Api.Models.Volunteers.VolunteerShift`. `startAt`/`endAt` are
 * absolute instants (ISO). Shift-level `capacity` (null = unlimited) applies only when the shift lists
 * no jobs; otherwise each entry in `roles` carries its own headcount.
 */
export interface IVolunteerShift {
	id: string;
	volunteerActivityId?: string;
	startAt?: string;
	endAt?: string;
	description?: string;
	location?: string;
	capacity?: number | null;
	roles?: IVolunteerShiftRole[];
	isArchived?: boolean;
}

export enum VolunteerFilterProperty {
	Date = 'Date',
	Activity = 'Activity',
	Shift = 'Shift',
	Status = 'Status',
	Location = 'Location',
	ContactId = 'ContactId',
	Tag = 'Tag',
	Role = 'Role',
}

export interface IVolunteerFilterCriteria {
	criteria?: IVolunteerFilterCriteria[];
	op?: FilterOperator;
	property?: VolunteerFilterProperty;
	value?: string;
}

export interface IVolunteerFilter {
	criteria?: IVolunteerFilterCriteria[];
	op?: FilterOperator;
}

export interface IVolunteerSummary {
	totalHours: number;
	totalVolunteers: number;
	participationCount: number;
	noShowCount: number;
	noShowRate: number;
	averageHoursPerVolunteer: number;
	donatedTimeValue: number;
	donatedTimeValueRatePerHour: number;
}

export interface IVolunteer {
	id: string;
	contactId: string;
	contact?: IProjectedContact;
	totalHours: number;
	activityIds?: string[];
	activityCount: number;
	participationCount: number;
	lastActivityDate?: string;
}

export interface IVolunteersFilterRequest {
	filter?: IVolunteerFilter;
	excludeActivityIds?: string[];
}

export enum VolunteerAttendanceStatus {
	Expected = 'Expected',
	CheckedIn = 'CheckedIn',
	CheckedOut = 'CheckedOut',
	NoShow = 'NoShow',
	Cancelled = 'Cancelled',
}

export enum VolunteerAttendanceSource {
	SurveySignup = 'SurveySignup',
	WalkIn = 'WalkIn',
	AdHoc = 'AdHoc',
	ManualSignup = 'ManualSignup',
}

export interface IVolunteerAttendance {
	id: string;
	contactId: string;
	contact?: IProjectedContact;
	volunteerActivityId?: string;
	volunteerShiftId?: string;
	roleId?: string;
	location?: string;
	date?: string;
	scheduledStartAt?: string;
	scheduledEndAt?: string;
	checkInAt?: string;
	checkOutAt?: string;
	hours?: number;
	status?: VolunteerAttendanceStatus;
	source?: VolunteerAttendanceSource;
	surveyResponseId?: string;
	tags?: string[];
}
